import React, { useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import {
	Heading,
	Box,
	Badge,
	Table,
	Tbody,
	Td,
	Th,
	Thead,
	Tr,
	Button,
	ButtonGroup,
	HStack,
	Spacer,
	useColorModeValue as mode,
} from '@chakra-ui/react'
import { MdDragHandle } from 'react-icons/md'
import { RiAddFill } from 'react-icons/ri'
import {
	DragDropContext,
	Droppable,
	Draggable,
	DraggingStyle,
	NotDraggingStyle,
	DropResult,
} from 'react-beautiful-dnd'
import { axios } from '../../../lib/axios-client'
import { useAuth } from '../../../context/auth'
import { Product } from '../../../types/product'
import { ProductBadgeEnum, ProductStatusEnum } from '../../../constants/product'
import { StorageImage } from '../../../components/StorageImage'

export default function ProductList() {
	const { isAuthenticated } = useAuth()
	const [products, setProducts] = useState<Product[]>([])

	const router = useRouter()

	useEffect(() => {
		const fetchProducts = async (page?: number) => {
			const res = await axios.get(
				page
					? `${process.env.REST_ENDPOINT}/admin/api/v1/products?page=${page}`
					: `${process.env.REST_ENDPOINT}/admin/api/v1/products`
			)
			setProducts(res.data)
		}

		isAuthenticated && fetchProducts(Number(router.query.page))
	}, [isAuthenticated, router.query])

	const columns = [
		{
			Header: '表示順',
			accessor: 'position',
		},
		{
			Header: '商品画像',
			accessor: 'images',
			Cell: function ImageCell(images: any) {
				return images.length > 0 && <StorageImage url={images[0].url} width="100px" />
			},
		},
		{
			Header: '商品',
			accessor: 'title',
		},
		{
			Header: '価格',
			accessor: 'price',
			Cell: function PriceCell(price: any) {
				return `${price.toLocaleString()}円`
			},
		},
		{
			Header: 'ステータス',
			accessor: 'status',
			Cell: function StatusCell(status: any) {
				return (
					<Badge fontSize="xs" colorScheme={ProductBadgeEnum[status]}>
						{ProductStatusEnum[status]}
					</Badge>
				)
			},
		},
	]

	const getListStyle = (isDraggingOver: boolean) => ({
		background: isDraggingOver ? 'lightgrey' : 'white',
		width: '100%',
	})

	const getItemStyle = (
		isDragging: boolean,
		draggableStyle: DraggingStyle | NotDraggingStyle | undefined
	) => ({
		background: 'white',
		width: '100%',

		// styles we need to apply on draggables
		...draggableStyle,
	})

	const onDragEnd = async (result: DropResult) => {
		if (result.destination) {
			try {
				const res = await axios.post(
					`${process.env.REST_ENDPOINT}/admin/api/v1/products/${result.draggableId}/reorder`,
					{
						newPosition: result.destination.index + 1,
					}
				)
				console.log(res)
				setProducts(res.data.data.products)
			} catch (error: any) {
				console.log(error)
			}
		}
		// console.log(result)
		// // dropped outside the list
		// if (!result.destination) {
		//   return;
		// }
		// const items = reorder(
		//   this.state.items,
		//   result.source.index,
		//   result.destination.index
		// );
		// this.setState({
		//   items
		// });
	}

	return (
		<>
			<Box as="section">
				<Box overflowX="auto">
					<HStack mb={4}>
						<Heading size="md">商品一覧</Heading>
						<Spacer />
						<ButtonGroup size="sm" variant="outline">
							<Button
								iconSpacing="1"
								leftIcon={<RiAddFill fontSize="1.25em" />}
								onClick={() => router.push('/settings/menu/products/new')}
							>
								商品を作成する
							</Button>
						</ButtonGroup>
					</HStack>
					<DragDropContext onDragEnd={onDragEnd}>
						<Droppable droppableId="droppable">
							{(provided, snapshot) => (
								<div
									{...provided.droppableProps}
									ref={provided.innerRef}
									style={getListStyle(snapshot.isDraggingOver)}
								>
									<Table my="8" borderWidth="1px" fontSize="sm">
										<Thead bg={mode('gray.50', 'gray.800')}>
											<Tr>
												<Th />
												{columns.map((column, index) => (
													<Th whiteSpace="nowrap" scope="col" key={index}>
														{column.Header}
													</Th>
												))}
												<Th />
											</Tr>
										</Thead>
										<Tbody>
											{products.map((row, index) => (
												<Draggable key={row.id} draggableId={String(row.id)} index={index}>
													{(provided, snapshot) => (
														<Tr
															ref={provided.innerRef}
															{...provided.draggableProps}
															{...provided.dragHandleProps}
															style={getItemStyle(
																snapshot.isDragging,
																provided.draggableProps.style
															)}
														>
															<Td whiteSpace="nowrap" key={index}>
																<MdDragHandle size={18} />
															</Td>
															{columns.map((column, index) => {
																const cell = column.accessor
																	? row[column.accessor as keyof typeof row]
																	: row
																const element = column.Cell?.(cell) ?? cell
																return (
																	<Td whiteSpace="nowrap" key={index}>
																		{element}
																	</Td>
																)
															})}
															<Td textAlign="right">
																<Button
																	variant="link"
																	colorScheme="blue"
																	onClick={() =>
																		router.push(`/settings/menu/products/${row.id}/edit`)
																	}
																>
																	編集
																</Button>
															</Td>
														</Tr>
													)}
												</Draggable>
											))}
										</Tbody>
									</Table>
								</div>
							)}
						</Droppable>
					</DragDropContext>
				</Box>
			</Box>
		</>
	)
}
